import { useMemo } from 'react';
import type { CampaignFormData } from '../types';
import SARIcon from '@/components/feature/SARIcon';
import SAR from '@/components/feature/SAR';

interface Step3Props {
  data: CampaignFormData;
  onChange: (field: keyof CampaignFormData, value: string) => void;
}

const durations = [
  { value: '15', label: '15 يوم', desc: 'للحملات العاجلة' },
  { value: '30', label: '30 يوم', desc: 'الأكثر شيوعاً' },
  { value: '45', label: '45 يوم', desc: 'للمشاريع الكبيرة' },
  { value: '60', label: '60 يوم', desc: 'للمشاريع طويلة المدى' },
];

// ─── IBAN Validation ──────────────────────────────────────────────────────────
// Saudi IBAN format: SA + 2 check digits + 20 alphanumeric chars = 24 chars total
const validateSaudiIBAN = (raw: string): { valid: boolean; message: string; status: 'empty' | 'invalid' | 'valid' | 'partial' } => {
  const clean = raw.replace(/\s/g, '').toUpperCase();

  if (clean.length === 0) return { valid: false, message: '', status: 'empty' };

  if (!clean.startsWith('SA')) {
    return { valid: false, message: 'الآيبان السعودي يجب أن يبدأ بـ SA', status: 'invalid' };
  }

  if (clean.length < 24) {
    return { valid: false, message: `${clean.length} / 24 حرف — أكمل رقم الآيبان`, status: 'partial' };
  }

  if (clean.length > 24) {
    return { valid: false, message: 'رقم الآيبان يجب أن يكون 24 حرفاً بالضبط', status: 'invalid' };
  }

  // Check digits must be numeric
  const checkDigits = clean.slice(2, 4);
  if (!/^\d{2}$/.test(checkDigits)) {
    return { valid: false, message: 'رقمان التحقق (الموضع 3-4) يجب أن يكونا أرقاماً', status: 'invalid' };
  }

  // Remaining 20 chars must be alphanumeric
  const bban = clean.slice(4);
  if (!/^[A-Z0-9]{20}$/.test(bban)) {
    return { valid: false, message: 'الآيبان يحتوي على أحرف غير صالحة', status: 'invalid' };
  }

  // MOD-97 check (ISO 7064)
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.split('').map((c) => {
    const code = c.charCodeAt(0);
    return code >= 65 ? String(code - 55) : c;
  }).join('');

  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 9) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 9)) % 97;
  }

  if (remainder !== 1) {
    return { valid: false, message: 'رقم الآيبان غير صحيح — تحقق من الأرقام مجدداً', status: 'invalid' };
  }

  return { valid: true, message: 'آيبان سعودي صحيح ✓', status: 'valid' };
};

// Format IBAN with spaces every 4 chars for readability
const formatIBAN = (raw: string): string => {
  const clean = raw.replace(/\s/g, '').toUpperCase().slice(0, 24);
  return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
};

// ─── Component ────────────────────────────────────────────────────────────────
const Step3Goal = ({ data, onChange }: Step3Props) => {
  const goal = Number(data.goalAmount) || 0;
  const platformFee = goal * 0.05;
  const netAmount = goal - platformFee;

  const formatNum = (n: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

  const ibanValidation = useMemo(() => validateSaudiIBAN(data.iban), [data.iban]);

  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\s/g, '').toUpperCase();
    // Only allow SA prefix + alphanumeric
    const filtered = raw.replace(/[^A-Z0-9]/g, '');
    onChange('iban', formatIBAN(filtered));
  };

  const ibanBorderClass = {
    empty: 'border-stone-200 focus:border-emerald-400',
    partial: 'border-amber-300 focus:border-amber-400',
    invalid: 'border-red-300 focus:border-red-400',
    valid: 'border-emerald-400 focus:border-emerald-500',
  }[ibanValidation.status];

  const ibanMsgClass = {
    empty: '',
    partial: 'text-amber-600',
    invalid: 'text-red-500',
    valid: 'text-emerald-600',
  }[ibanValidation.status];

  const ibanIcon = {
    empty: null,
    partial: <i className="ri-time-line text-amber-500 text-sm"></i>,
    invalid: <i className="ri-close-circle-line text-red-500 text-sm"></i>,
    valid: <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>,
  }[ibanValidation.status];

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h2 className="text-xl font-black text-stone-800 mb-1">الهدف المالي والمدة</h2>
        <p className="text-stone-500 text-sm">حدد كم تحتاج وكم من الوقت</p>
      </div>

      {/* Goal Amount */}
      <div>
        <label className="block text-sm font-semibold text-stone-700 mb-2">
          المبلغ المستهدف (ريال سعودي) <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <input
            type="number"
            value={data.goalAmount}
            onChange={(e) => onChange('goalAmount', e.target.value)}
            placeholder="0"
            min={1000}
            max={500000}
            className="w-full border border-stone-200 rounded-xl px-4 py-4 text-2xl font-black text-stone-800 focus:outline-none focus:border-emerald-400 text-right pr-16"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400"><SARIcon size={14} /></span>
        </div>

        {/* Quick Amounts */}
        <div className="flex flex-wrap gap-2 mt-3">
          {[5000, 10000, 25000, 50000, 100000].map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => onChange('goalAmount', String(amt))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                data.goalAmount === String(amt)
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-emerald-300'
              }`}
            >
              {formatNum(amt)}
            </button>
          ))}
        </div>

        {/* Fee Breakdown */}
        {goal > 0 && (
          <div className="mt-4 bg-stone-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-stone-500">المبلغ المستهدف</span>
              <span className="font-semibold text-stone-800"><SAR amount={goal} iconSize={12} /></span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-500">رسوم المنصة (5%)</span>
              <span className="text-stone-500">- <SAR amount={platformFee} iconSize={12} /></span>
            </div>
            <div className="border-t border-stone-200 pt-2 flex justify-between text-sm">
              <span className="font-semibold text-stone-700">ستستلم تقريباً</span>
              <span className="font-black text-emerald-600"><SAR amount={netAmount} iconSize={12} /></span>
            </div>
          </div>
        )}
      </div>

      {/* Duration */}
      <div>
        <label className="block text-sm font-semibold text-stone-700 mb-3">
          مدة الحملة <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {durations.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => onChange('duration', d.value)}
              className={`p-4 rounded-xl border-2 text-right transition-all cursor-pointer ${
                data.duration === d.value
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <div className={`text-lg font-black mb-0.5 ${data.duration === d.value ? 'text-emerald-700' : 'text-stone-800'}`}>
                {d.label}
              </div>
              <div className="text-xs text-stone-400">{d.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Bank Info */}
      <div>
        <label className="block text-sm font-semibold text-stone-700 mb-2">
          رقم الآيبان البنكي (IBAN) <span className="text-red-400">*</span>
        </label>

        {/* Format hint */}
        <p className="text-xs text-stone-400 mb-2">
          مثال: <span className="font-mono tracking-wider">SA03 8000 0000 6080 1016 7519</span>
        </p>

        <div className="relative">
          <input
            type="text"
            value={data.iban}
            onChange={handleIBANChange}
            placeholder="SA00 0000 0000 0000 0000 0000"
            maxLength={29}
            className={`w-full border-2 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none text-right font-mono tracking-wider placeholder-stone-300 transition-colors ${ibanBorderClass}`}
            dir="ltr"
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {/* Status icon inside input */}
          {ibanValidation.status !== 'empty' && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              {ibanIcon}
            </div>
          )}
        </div>

        {/* Validation message */}
        <div className="flex items-center gap-1.5 mt-2 min-h-[18px]">
          {ibanValidation.status === 'empty' ? (
            <>
              <i className="ri-shield-check-line text-emerald-500 text-xs"></i>
              <p className="text-xs text-stone-400">معلوماتك البنكية مشفرة وآمنة تماماً</p>
            </>
          ) : (
            <>
              {ibanIcon}
              <p className={`text-xs font-medium ${ibanMsgClass}`}>{ibanValidation.message}</p>
            </>
          )}
        </div>

        {/* Character counter */}
        {ibanValidation.status !== 'empty' && (
          <div className="flex justify-end mt-1">
            <span className={`text-xs font-mono ${
              ibanValidation.status === 'valid' ? 'text-emerald-500' : 'text-stone-400'
            }`}>
              {data.iban.replace(/\s/g, '').length} / 24
            </span>
          </div>
        )}
      </div>

      {/* Terms */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-amber-600 text-lg mt-0.5 shrink-0"></i>
          <div>
            <p className="text-sm font-semibold text-amber-800 mb-1">تنبيه مهم</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              سيتم مراجعة حملتك من فريق هِبة خلال 24-48 ساعة قبل النشر. تأكد من أن جميع المعلومات صحيحة ودقيقة.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3Goal;
